import React from "react";
import {useTranslation} from "react-i18next";
import "./../help-mockup.css";

type ShellProps = {
    caption: string;
    children: React.ReactNode;
};

const MockShell: React.FC<ShellProps> = ({caption, children}) => (
    <div className="help-mockup" aria-hidden="true">
        <div className="help-mockup__caption">{caption}</div>
        <div className="help-mockup__frame">
            <div className="help-mockup__chrome">
                <span className="help-mockup__dot"/>
                <span className="help-mockup__dot"/>
                <span className="help-mockup__dot"/>
            </div>
            {children}
        </div>
    </div>
);

export const GettingStartedMockup: React.FC = () => {
    const {t} = useTranslation();
    const steps = [
        t("help.mock.connect"),
        t("help.mock.configure"),
        t("help.mock.schedule"),
        t("help.mock.sync"),
    ];

    return (
        <MockShell caption={t("help.mock.gettingStartedCaption")}>
            <div className="help-mockup__steps">
                {steps.map((label, index) => (
                    <React.Fragment key={label}>
                        {index > 0 ? (
                            <span className="help-mockup__arrow help-mockup__arrow--desktop">→</span>
                        ) : null}
                        <div className="help-mockup__step">
                            <div className="help-mockup__step-num">{index + 1}</div>
                            <div className="help-mockup__step-label">{label}</div>
                        </div>
                    </React.Fragment>
                ))}
            </div>
        </MockShell>
    );
};

export const DraftBookedMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.draftBookedCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">{t("help.mock.manualBook")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <div className="help-mockup__line help-mockup__line--short"/>
                        <span className="help-mockup__badge help-mockup__badge--draft">
                            {t("help.mock.draft")}
                        </span>
                        <div className="help-mockup__flow-label">{t("help.mock.youBookInEconomic")}</div>
                    </div>
                </div>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.autoBook")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <div className="help-mockup__line help-mockup__line--short"/>
                        <span className="help-mockup__badge help-mockup__badge--booked">
                            {t("help.mock.booked")}
                        </span>
                        <div className="help-mockup__flow-label">{t("help.mock.appBooksAfterImport")}</div>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const MatchingMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.matchingCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">{t("help.mock.payment")}</div>
                        <div className="help-mockup__line help-mockup__line--short"/>
                        <span className="help-mockup__badge help-mockup__badge--wait">
                            {t("help.mock.waitingInvoice")}
                        </span>
                    </div>
                </div>
                <span className="help-mockup__arrow">→</span>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.invoiceOrCn")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <span className="help-mockup__badge help-mockup__badge--booked">
                            {t("help.mock.booked")}
                        </span>
                    </div>
                </div>
                <span className="help-mockup__arrow">→</span>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.matched")}</div>
                        <div className="help-mockup__line help-mockup__line--short"/>
                        <span className="help-mockup__badge help-mockup__badge--booked">
                            {t("help.mock.cashbookPosted")}
                        </span>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const NoticeErrorMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.noticeErrorCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">{t("help.mock.logRow")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <span className="help-mockup__badge help-mockup__badge--error">
                            {t("help.mock.error")}
                        </span>
                        <div className="help-mockup__flow-label">{t("help.mock.errorHint")}</div>
                    </div>
                </div>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.logRow")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <span className="help-mockup__badge help-mockup__badge--notice">
                            {t("help.mock.notice")}
                        </span>
                        <div className="help-mockup__flow-label">{t("help.mock.noticeHint")}</div>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const ModulesToggleMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.modulesCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.invoiceTitle")}</div>
                        <span className="help-mockup__badge help-mockup__badge--on">
                            {t("help.mock.syncOn")}
                        </span>
                    </div>
                </div>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--muted">
                        <div className="help-mockup__title">{t("help.payoutTitle")}</div>
                        <span className="help-mockup__badge help-mockup__badge--off">
                            {t("help.mock.syncOff")}
                        </span>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const ScheduleMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.scheduleCaption")}>
            <table className="help-mockup__table">
                <thead>
                <tr>
                    <th>{t("help.mock.type")}</th>
                    <th>{t("help.mock.frequency")}</th>
                    <th>{t("help.mock.status")}</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td>{t("help.invoiceTitle")}</td>
                    <td>{t("help.mock.hourly")}</td>
                    <td>
                        <span className="help-mockup__badge help-mockup__badge--on">
                            {t("help.mock.active")}
                        </span>
                    </td>
                </tr>
                <tr>
                    <td>{t("help.refundTitle")}</td>
                    <td>{t("help.mock.hourly")}</td>
                    <td>
                        <span className="help-mockup__badge help-mockup__badge--on">
                            {t("help.mock.active")}
                        </span>
                    </td>
                </tr>
                <tr>
                    <td>{t("help.paymentTitle")}</td>
                    <td>{t("help.mock.daily")}</td>
                    <td>
                        <span className="help-mockup__badge help-mockup__badge--off">
                            {t("help.mock.inactive")}
                        </span>
                    </td>
                </tr>
                </tbody>
            </table>
        </MockShell>
    );
};

export const InvoiceFlowMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.invoiceCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">Stripe</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <div className="help-mockup__flow-label">in_… / cs_…</div>
                    </div>
                </div>
                <span className="help-mockup__arrow">→</span>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">e-conomic</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <span className="help-mockup__badge help-mockup__badge--draft">
                            {t("help.mock.draft")}
                        </span>
                        <span className="help-mockup__badge help-mockup__badge--booked ms-1">
                            {t("help.mock.booked")}
                        </span>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const RefundFlowMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.refundCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">Stripe re_…</div>
                        <div className="help-mockup__line help-mockup__line--short"/>
                        <div className="help-mockup__flow-label">{t("help.mock.needsInvoiceSynced")}</div>
                    </div>
                </div>
                <span className="help-mockup__arrow">→</span>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.creditNote")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <div className="help-mockup__neg">qty −1 / −n</div>
                        <span className="help-mockup__badge help-mockup__badge--draft">
                            {t("help.mock.draft")}
                        </span>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const PaymentFlowMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.paymentCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">Stripe charge</div>
                        <div className="help-mockup__line help-mockup__line--short"/>
                    </div>
                </div>
                <span className="help-mockup__arrow">→</span>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.cashbook")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <div className="help-mockup__flow-label">{t("help.mock.matchSameOtherRef")}</div>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const CommonSetupMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.commonCaption")}>
            <div className="help-mockup__card help-mockup__card--active">
                <div className="help-mockup__title">{t("help.commonTitle")}</div>
                <div className="help-mockup__line help-mockup__line--mid"/>
                <div className="help-mockup__flow-label">{t("help.mock.customerGroup")}</div>
                <div className="help-mockup__line help-mockup__line--short"/>
                <div className="help-mockup__flow-label">{t("help.mock.productGroup")}</div>
            </div>
        </MockShell>
    );
};

export const PayoutFlowMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.payoutCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">Stripe payout</div>
                        <div className="help-mockup__line help-mockup__line--short"/>
                    </div>
                </div>
                <span className="help-mockup__arrow">→</span>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.bankFeeAccounts")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const SubscriptionFlowMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.subscriptionCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">Stripe</div>
                        <div className="help-mockup__line help-mockup__line--short"/>
                        <div className="help-mockup__flow-label">{t("help.mock.paidSubscriptionInvoice")}</div>
                    </div>
                </div>
                <span className="help-mockup__arrow">→</span>
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.balanceSales")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <div className="help-mockup__flow-label">{t("help.mock.accrualOpen")}</div>
                        <div className="help-mockup__flow-label">{t("help.mock.accrualMonthly")}</div>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

export const AccountingModeMockup: React.FC = () => {
    const {t} = useTranslation();

    return (
        <MockShell caption={t("help.mock.accountingCaption")}>
            <div className="help-mockup__row">
                <div className="help-mockup__col">
                    <div className="help-mockup__card help-mockup__card--active">
                        <div className="help-mockup__title">{t("help.mock.modeMatching")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <div className="help-mockup__flow-label">{t("help.mock.modeMatchingHint")}</div>
                        <span className="help-mockup__badge help-mockup__badge--on">
                            {t("help.mock.default")}
                        </span>
                    </div>
                </div>
                <div className="help-mockup__col">
                    <div className="help-mockup__card">
                        <div className="help-mockup__title">{t("help.mock.modeLedger")}</div>
                        <div className="help-mockup__line help-mockup__line--mid"/>
                        <div className="help-mockup__flow-label">{t("help.mock.modeLedgerHint")}</div>
                        <span className="help-mockup__badge help-mockup__badge--wait">
                            {t("help.mock.optIn")}
                        </span>
                    </div>
                </div>
            </div>
        </MockShell>
    );
};

const MOCKUP_BY_ID: Record<string, React.FC> = {
    "getting-started": GettingStartedMockup,
    "accounting-mode": AccountingModeMockup,
    "draft-vs-booked": DraftBookedMockup,
    matching: MatchingMockup,
    notice: NoticeErrorMockup,
    "modules-toggle": ModulesToggleMockup,
    schedule: ScheduleMockup,
    common: CommonSetupMockup,
    invoice: InvoiceFlowMockup,
    refund: RefundFlowMockup,
    payment: PaymentFlowMockup,
    payout: PayoutFlowMockup,
    subscription: SubscriptionFlowMockup,
};

export const HelpMockup: React.FC<{sectionId: string}> = ({sectionId}) => {
    const Component = MOCKUP_BY_ID[sectionId];
    if (!Component) {
        return null;
    }
    return <Component/>;
};

export default HelpMockup;
